import { FC, useEffect } from 'react';
import { css, Interpolation, Theme } from '@emotion/react';
import { useInView } from 'react-intersection-observer';
import { InsuranceDetailQueries } from '@frontend/api-insurance-detail';
import { PlatformLocationFeatureV1 } from '@frontend/api-platform-location-feature';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { useLocationIdsWithAccess } from '@frontend/insurance-verification';
import { breakpoints } from '@frontend/responsiveness';
import { pendo } from '@frontend/tracking';
import { theme } from '@frontend/theme';
import { Button, useModalControl } from '@frontend/design-system';
import { EIVDashboardTrialWidgetTrackingIds } from '../../../../constants/tracking-ids';
import { OnboardingItem } from './onboarding/onboarding-item';
import { OnboardingModal } from './onboarding/onboarding-modal';
import { PayersList } from './payers-list';
import { SkipEligibilitySetupModal } from './skip-eligibility-setup-modal';
import { WidgetLayout } from './widget-layout';

interface InsuranceEligibilityEnabledProps {
  containerStyles?: Interpolation<Theme>;
  onSkipClick: () => void;
}

export const InsuranceEligibilityEnabled: FC<InsuranceEligibilityEnabledProps> = ({ containerStyles, onSkipClick }) => {
  const { t } = useTranslation('featurePromotion', { keyPrefix: 'weave-insurance-eligibility' });

  const { modalProps: connectPayerModalProps, triggerProps: connectPayerModalTriggerProps } = useModalControl();
  const { modalProps: favoriteCDTModalProps, triggerProps: favoriteCDTTriggerProps } = useModalControl();
  const { modalProps: setUpDeltaDentalModalProps, triggerProps: setUpDeltaDentalTriggerProps } = useModalControl();
  const { locationIdsWithAccess } = useLocationIdsWithAccess();
  const { data: onboardingBannerStatus, isLoading: isLoadingOnboardingBannerStatus } =
    InsuranceDetailQueries.useGetInsuranceEligibilityOnBoardingBannerStatus(locationIdsWithAccess);

  const { data: widgetState, isLoading: isLoadingWidgetState } =
    PlatformLocationFeatureV1.Queries.useCheckFeatureNotificationStatusQuery({
      request: {
        notificationFeature: PlatformLocationFeatureV1.Types.NotificationFeature.NOTIFICATION_FEATURE_EIV_SETUP,
        sourceEnum: PlatformLocationFeatureV1.Types.Source.SOURCE_DASHBOARD_TEASER_BANNER,
      },
      options: {
        retry: false,
        select: (response) => response.currentStatus,
        enabled: true,
      },
    });

  const isOnboardingCompleted =
    onboardingBannerStatus?.isFavoriteCdtCodesAddedStepCompleted &&
    onboardingBannerStatus?.isMfaPayerConnectedStepCompleted &&
    onboardingBannerStatus?.isPayerConnectedStepCompleted;

  const isLoading = isLoadingOnboardingBannerStatus || isLoadingWidgetState;

  const { ref, inView } = useInView({
    triggerOnce: true,
  });

  useEffect(() => {
    if (inView) {
      pendo.track(EIVDashboardTrialWidgetTrackingIds.featureEnabledWidgetVisible);
    }
  }, [inView]);

  if (
    isLoading ||
    isOnboardingCompleted ||
    widgetState === PlatformLocationFeatureV1.Types.Action.ACTION_NEVER_SHOW_AGAIN
  ) {
    return null;
  }

  return (
    <>
      <WidgetLayout
        containerStyles={containerStyles}
        isAccordion={true}
        innerRef={ref}
        headerActions={<InsuranceEligibilityHeaderActions onSkipClick={onSkipClick} />}
        isFreeTrialWidget={false}
        noBorder
      >
        <div css={defaultStyles.body}>
          <div css={defaultStyles.banner}>
            <OnboardingItem
              title={t('Connect Payers')}
              itemType='CONNECT_PAYERS'
              icon={<Icon name='alert' color='error' size={16} />}
              isOnboardingCompleted={!!onboardingBannerStatus?.isPayerConnectedStepCompleted}
              triggerProps={connectPayerModalTriggerProps}
            />
            <OnboardingItem
              title={t('Set Up Delta Dental MFA')}
              itemType='SET_UP_DELTA_DENTAL_MFA'
              isOnboardingCompleted={!!onboardingBannerStatus?.isMfaPayerConnectedStepCompleted}
              triggerProps={setUpDeltaDentalTriggerProps}
            />
            <OnboardingItem
              title={t('Favorite CDT Code')}
              itemType='FAVORITE_CDT_CODES'
              isOnboardingCompleted={!!onboardingBannerStatus?.isFavoriteCdtCodesAddedStepCompleted}
              triggerProps={favoriteCDTTriggerProps}
            />
          </div>
          <PayersList />
        </div>
      </WidgetLayout>
      <OnboardingModal modalProps={connectPayerModalProps} onboaringModalID='CONNECT_PAYERS' />
      <OnboardingModal modalProps={setUpDeltaDentalModalProps} onboaringModalID='SET_UP_DELTA_DENTAL_MFA' />
      <OnboardingModal modalProps={favoriteCDTModalProps} onboaringModalID='FAVORITE_CDT_CODES' />
    </>
  );
};

const InsuranceEligibilityHeaderActions: FC<{ onSkipClick: () => void }> = ({ onSkipClick }) => {
  const { modalProps, triggerProps } = useModalControl();
  const { t } = useTranslation('featurePromotion', { keyPrefix: 'weave-insurance-eligibility' });

  return (
    <div css={defaultStyles.headerActions}>
      <Button variant='tertiary' {...triggerProps} css={{ color: theme.colors.neutral50 }}>
        {t('Skip')}
      </Button>
      <SkipEligibilitySetupModal modalProps={modalProps} onSkipClick={onSkipClick} />
    </div>
  );
};

const defaultStyles = {
  body: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(2)};
  `,
  banner: css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(1)};
    padding: ${theme.spacing(1)};
    border-radius: ${theme.borderRadius.small};
    width: 100%;
    @container (max-width: ${breakpoints.mobile.max}px) {
      flex-direction: column;
    }
  `,
  headerActions: css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(1)};
  `,
  bannerIcon: css`
    flex-shrink: 0;
    padding: ${theme.spacing(0.5)};
    color: ${theme.colors.tangerine40};
    background-color: ${theme.colors.white};
    border-radius: ${theme.borderRadius.full};
  `,
  bannerText: css`
    color: ${theme.colors.tangerine50};
  `,
  daysRemainingChip: css`
    max-width: max-content;
    background-color: ${theme.colors.white};
    color: ${theme.colors.secondary.seaweed50};
  `,
};
